import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { ChildrenPropType } from '../../../../prop-types/children';
import { useModal } from '../../../../hooks/use-modal';
import updateShipstationSellerProviderSettings from './api';
import ShipstationSettingModal from './index';
import { refreshLabelProviders } from '../../../../store/seller/seller.actions';
import { useSellerLabelProvider } from '../../../../hooks/use-seller-label-provider';

import messages from './messages';

const ShipstationSettingContext = React.createContext();

export const useShipstationSettingContext = () => useContext(ShipstationSettingContext);

const ShipstationSettingModalContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { close, isOpen, open } = useModal();
  const [hideIsPrimaryAccount, setHideIsPrimaryAccount] = useState(false);
  const [selectedSellerProviderId, setSelectedLabelProviderId] = useAsyncSafeState(null);
  const selectedProvider = useSellerLabelProvider(selectedSellerProviderId);

  const onEditProviderSS = useCallback(
    (sellerLabelProviderId, shouldHideIsPrimaryAccount = false) => {
      setSelectedLabelProviderId(sellerLabelProviderId);
      setHideIsPrimaryAccount(shouldHideIsPrimaryAccount);
      open();
    },
    [open, setSelectedLabelProviderId],
  );

  const onSave = useCallback(
    (sellerLabelProviderId) => {
      dispatch(refreshLabelProviders({ shouldRefreshServices: false, sellerLabelProviderId }));
      setSelectedLabelProviderId(null);
      close();
    },
    [close, dispatch, setSelectedLabelProviderId],
  );

  const onClose = useCallback(() => {
    setSelectedLabelProviderId(null);
    close();
  }, [close, setSelectedLabelProviderId]);

  const saveSetting = useCallback(
    async (sellerLabelProviderId, _nickname, _isDefault) => {
      try {
        await updateShipstationSellerProviderSettings(sellerLabelProviderId, _nickname, _isDefault);
        onSave(sellerLabelProviderId);
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.savingError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      }
    },
    [onSave],
  );

  const value = useMemo(() => {
    return {
      onEditProviderSS,
      saveSetting,
    };
  }, [onEditProviderSS, saveSetting]);

  return (
    <ShipstationSettingContext.Provider value={value}>
      {children}
      {isOpen && !!selectedSellerProviderId && !!selectedProvider && (
        <ShipstationSettingModal
          hideIsPrimaryAccount={hideIsPrimaryAccount}
          onClose={onClose}
          onSave={onSave}
          sellerLabelProviderId={selectedSellerProviderId}
        />
      )}
    </ShipstationSettingContext.Provider>
  );
};

ShipstationSettingModalContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};

export default ShipstationSettingModalContextProvider;
